import { useState, useEffect, useMemo } from "react";
import depinLogo from "@/assets/depin-logo-new.png";

interface LoadingScreenProps {
  onComplete: () => void;
}

// Particle component
const Particle = ({
  delay,
  duration,
  startX,
  startY,
  endX,
  endY,
}: {
  delay: number;
  duration: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) => (
  <div
    className="absolute w-1 h-1 bg-primary rounded-full opacity-0"
    style={
      {
        left: `${startX}%`,
        top: `${startY}%`,
        animation: `particleFloat ${duration}s ease-in-out ${delay}s infinite`,
        "--end-x": `${endX - startX}vw`,
        "--end-y": `${endY - startY}vh`,
      } as React.CSSProperties
    }
  />
);

// Network node component
const NetworkNode = ({ x, y, delay, size }: { x: number; y: number; delay: number; size: number }) => (
  <div
    className="absolute rounded-full bg-primary/60"
    style={{
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
      animation: `nodePulse 2s ease-in-out ${delay}s infinite`,
    }}
  />
);

// Network line component
const NetworkLine = ({ x1, y1, x2, y2, delay }: { x1: number; y1: number; x2: number; y2: number; delay: number }) => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ animation: `lineFade 3s ease-in-out ${delay}s infinite` }}
  >
    <line
      x1={`${x1}%`}
      y1={`${y1}%`}
      x2={`${x2}%`}
      y2={`${y2}%`}
      stroke="hsl(var(--primary))"
      strokeWidth="1"
      strokeOpacity="0.3"
    />
  </svg>
);

const LoadingScreen = ({ onComplete }: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  // Generate particles
  const particles = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        delay: Math.random() * 3,
        duration: 3 + Math.random() * 2,
        startX: Math.random() * 100,
        startY: Math.random() * 100,
        endX: Math.random() * 100,
        endY: Math.random() * 100,
      })),
    [],
  );

  // Generate network nodes around center
  const networkNodes = useMemo(() => {
    const nodes = [];
    const centerX = 50;
    const centerY = 50;

    // Center node
    nodes.push({ id: 0, x: centerX, y: centerY, delay: 0, size: 8 });

    // Inner ring
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      nodes.push({
        id: i + 1,
        x: centerX + Math.cos(angle) * 12,
        y: centerY + Math.sin(angle) * 12,
        delay: i * 0.1,
        size: 5,
      });
    }

    // Outer ring
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2 + 0.3;
      nodes.push({
        id: i + 7,
        x: centerX + Math.cos(angle) * 22,
        y: centerY + Math.sin(angle) * 22,
        delay: i * 0.08,
        size: 3,
      });
    }

    return nodes;
  }, []);

  // Generate network lines
  const networkLines = useMemo(() => {
    const lines = [];
    const centerX = 50;
    const centerY = 50;

    // Connect inner ring to center
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      lines.push({
        id: i,
        x1: centerX,
        y1: centerY,
        x2: centerX + Math.cos(angle) * 12,
        y2: centerY + Math.sin(angle) * 12,
        delay: i * 0.15,
      });
    }

    // Connect outer to inner
    for (let i = 0; i < 10; i++) {
      const outerAngle = (i / 10) * Math.PI * 2 + 0.3;
      const nearestInner = Math.round((i / 10) * 6) % 6;
      const innerAngle = (nearestInner / 6) * Math.PI * 2;
      lines.push({
        id: i + 6,
        x1: centerX + Math.cos(innerAngle) * 12,
        y1: centerY + Math.sin(innerAngle) * 12,
        x2: centerX + Math.cos(outerAngle) * 22,
        y2: centerY + Math.sin(outerAngle) * 22,
        delay: i * 0.1 + 0.5,
      });
    }

    return lines;
  }, []);

  useEffect(() => {
    const duration = 2500;
    const interval = 20;
    const increment = (100 * interval) / duration;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsExiting(true);
            setTimeout(onComplete, 500);
          }, 200);
          return 100;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center transition-opacity duration-500 overflow-hidden ${
        isExiting ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Animated particles */}
      {particles.map((p) => (
        <Particle key={p.id} {...p} />
      ))}

      {/* Network visualization */}
      <div className="absolute inset-0">
        {networkLines.map((line) => (
          <NetworkLine key={line.id} {...line} />
        ))}
        {networkNodes.map((node) => (
          <NetworkNode key={node.id} {...node} />
        ))}
      </div>

      {/* Pulsing rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="absolute w-48 h-48 rounded-full border border-primary/20 animate-[ringPulse_2s_ease-out_infinite]" />
        <div className="absolute w-72 h-72 rounded-full border border-primary/15 animate-[ringPulse_2s_ease-out_0.5s_infinite]" />
        <div className="absolute w-96 h-96 rounded-full border border-primary/10 animate-[ringPulse_2s_ease-out_1s_infinite]" />
      </div>

      {/* Central content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo with glow */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-primary/40 rounded-full blur-3xl animate-[glowPulse_2s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-[glowPulse_2s_ease-in-out_0.5s_infinite]" />
          <img src={depinLogo} alt="DePIN Land" width={96} height={96} className="relative w-24 h-24 object-contain" />
        </div>

        {/* Brand name */}
        <h1 className="text-3xl font-bold text-foreground mb-2">
          DePIN <span className="text-primary">Land</span>
        </h1>
        <p className="text-muted-foreground mb-8 text-sm">Empowering Africa Through DePIN</p>

        {/* Progress bar */}
        <div className="w-64 h-1.5 bg-muted/50 rounded-full overflow-hidden backdrop-blur-sm">
          <div
            className="h-full bg-gradient-to-r from-primary/80 to-primary rounded-full transition-all duration-100 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 bg-primary/50 blur-sm" />
          </div>
        </div>

        {/* Loading text */}
        <p className="mt-4 text-sm text-muted-foreground animate-pulse">Initializing network...</p>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes particleFloat {
          0%, 100% { opacity: 0; transform: translate(0, 0) scale(0.5); }
          50% { opacity: 0.8; transform: translate(var(--end-x), var(--end-y)) scale(1); }
        }
        @keyframes nodePulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); box-shadow: 0 0 20px hsl(var(--primary)); }
        }
        @keyframes lineFade {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.6; }
        }
        @keyframes ringPulse {
          0% { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
